import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiClient } from '../services/api';
import { PenkiBadge } from './PenkiBadge';
import { PenkiClaimCard } from './PenkiClaimCard';
import { formatPenki } from '../utils/penki';
import './ProfilePage.css';

interface LogoutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const LogoutConfirmModal: React.FC<LogoutConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    onConfirm();
  };

  const handleCancel = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    onClose();
  };

  return (
    <div className="modal-overlay logout-modal-overlay" onClick={onClose}>
      <div className="logout-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="logout-modal-header">
          <h2 className="logout-modal-title">Подтверждение выхода</h2>
          <button
            className="logout-modal-close"
            onClick={handleCancel}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        <p className="logout-modal-text">Вы уверены, что хотите выйти?</p>
        <div className="logout-modal-actions">
          <button
            className="button--primary button--full logout-modal-btn"
            onClick={handleConfirm}
          >
            Да, выйти
          </button>
          <button
            className="button--secondary button--full logout-modal-btn"
            onClick={handleCancel}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
};

interface NavTileProps {
  icon: string;
  label: string;
  description?: string;
  to: string;
  showDot?: boolean;
}

const NavTile: React.FC<NavTileProps> = ({ icon, label, description, to, showDot }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    navigate(to);
  };

  return (
    <button className="nav-tile" onClick={handleClick}>
      <img src={icon} alt="" className="nav-tile-icon" />
      <div className="nav-tile-content">
        <div className="nav-tile-label-wrap">
          <span className="nav-tile-label">
            {label}
            {showDot && <span className="nav-tile-dot" aria-label="Есть непрочитанные сообщения" />}
          </span>
          {description && <span className="nav-tile-description">{description}</span>}
        </div>
      </div>
      <img src="/chevron.png" alt="" className="nav-tile-arrow" />
    </button>
  );
};

const InviteFriendBlock: React.FC = () => {
  const [referralLink, setReferralLink] = useState<string | null>(null);
  const [referralsCount, setReferralsCount] = useState(0);
  const [penkiEarned, setPenkiEarned] = useState(0);
  const [bonusPercent, setBonusPercent] = useState(10);
  const [inviteBonus, setInviteBonus] = useState(50);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getReferralInfo()
      .then((res) => {
        if (cancelled) return;
        setReferralLink(res.referral_info?.referral_link ?? null);
        setReferralsCount(res.referral_info?.referrals_count ?? 0);
        setPenkiEarned(res.referral_info?.penki_earned ?? 0);
        setBonusPercent(res.referral_info?.bonus_percent ?? 10);
        setInviteBonus(res.referral_info?.invite_bonus ?? 50);
      })
      .catch(() => {
        if (!cancelled) setReferralLink(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!referralLink) return;
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [referralLink]);

  if (!referralLink) return null;

  return (
    <div className="profile-nav-section">
      <div className="profile-invite-block">
        <div className="profile-invite-info">
          <span className="profile-invite-title">Пригласить друга</span>
          <span className="profile-invite-subtitle">
            {inviteBonus} УЙМ за друга и {bonusPercent}% с его сборов
          </span>
          <span className="profile-invite-link">{referralLink}</span>
          <span className="profile-invite-stats">
            Приглашено: {referralsCount} · Заработано: {formatPenki(penkiEarned)}
          </span>
        </div>
        <button
          type="button"
          className="profile-invite-copy-btn"
          onClick={handleCopy}
          title="Копировать"
          aria-label="Копировать ссылку"
        >
          {copied ? (
            <svg
              className="profile-invite-copy-check"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export const ProfilePage: React.FC = () => {
  const { user, pendingActor, isLoading } = useUser();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  // Pending actors (no SBP linked yet) are known unverified up-front; for them we
  // skip the /onboarding-status round-trip and show "Привязка СБП" immediately.
  const [sbpCompleted, setSbpCompleted] = useState<boolean | null>(user ? null : false);
  const [stagesTo, setStagesTo] = useState<'/roadmap' | '/white-list'>('/roadmap');
  const [supportHasUnread, setSupportHasUnread] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [status, stats] = await Promise.all([
          apiClient.getOnboardingStatus(),
          apiClient.getWhiteListStats().catch(() => null),
        ]);
        setSbpCompleted(status.sbp_completed);
        if (stats?.white_list_start_date) {
          const startTime = new Date(stats.white_list_start_date).getTime();
          setStagesTo(Date.now() < startTime ? '/white-list' : '/roadmap');
        }
      } catch {
        setSbpCompleted(false); // при ошибке показываем пункт, чтобы пользователь мог попробовать
      }
    };
    if (user || pendingActor) load();
  }, [user, pendingActor]);

  useEffect(() => {
    if (!(user || pendingActor)) return;
    apiClient
      .getSupportUnread()
      .then(setSupportHasUnread)
      .catch(() => setSupportHasUnread(false));
  }, [user, pendingActor]);

  const performLogout = useCallback(() => {
    setLogoutModalOpen(false);
    localStorage.removeItem('telegram_login_widget_data');
    // Only use WebApp.close when in real Telegram WebApp (has initData)
    if (window.Telegram?.WebApp?.initData && window.Telegram?.WebApp?.close) {
      window.Telegram.WebApp.close();
    } else {
      window.location.href = '/auth/telegram';
    }
  }, []);

  const handleLogoutClick = useCallback(() => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    setLogoutModalOpen(true);
  }, []);

  const profile = user ?? pendingActor;

  if (isLoading || !profile) {
    return (
      <div className="page-container profile-page">
        <div className="profile-header">
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  const firstName = profile.first_name ?? '';
  const lastName = profile.last_name ?? '';
  const userName = firstName + (lastName ? ` ${lastName}` : '');
  const userAvatar = profile.avatar_url || '/profile.png';
  const userUsername = profile.username
    ? `@${profile.username}`
    : profile.telegram_id
      ? `ID: ${profile.telegram_id}`
      : profile.max_id
        ? `MAX: ${profile.max_id}`
        : '';

  return (
    <div className="page-container profile-page">
      <PenkiBadge />

      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          <img src={userAvatar} alt={userName} />
        </div>
        <h1 className="profile-name">{userName}</h1>
        <p className="profile-username">{userUsername}</p>
      </div>

      {user && <PenkiClaimCard />}

      {/* Block 1: Привязка, Этапы, Настройки, История транзакций */}
      <div className="profile-nav-section">
        <div className="profile-nav-tiles">
          {sbpCompleted === false && (
            <NavTile
              icon="/icon-sbp.png"
              label="Привязка СБП"
              to="/sbp-linking"
            />
          )}
          <NavTile
            icon="/icon-steps.png"
            label="Этапы ху#ков"
            to={stagesTo}
          />
          <NavTile
            icon="/icon-settings.png"
            label="Настройки"
            to="/settings"
          />
          <NavTile
            icon="/icon-clock.png"
            label="История транзакций"
            to="/transactions"
          />
        </div>
      </div>

      {/* Block 2: FAQ, Поддержка */}
      <div className="profile-nav-section">
        <div className="profile-nav-tiles">
          <NavTile
            icon="/icon-help.png"
            label="FAQ"
            to="/faq"
          />
          <NavTile
            icon="/phone.png"
            label="Поддержка"
            to="/support"
            showDot={supportHasUnread}
          />
        </div>
      </div>

      {/* Invite a friend — verified users only (referral-info requires verified.user) */}
      {user && <InviteFriendBlock />}

      {/* Logout */}
      <div className="profile-logout-section">
        <button
          className="profile-logout-button"
          onClick={handleLogoutClick}
        >
          <img src="/exit.png" alt="" className="profile-logout-icon" />
          <span>Выйти</span>
        </button>
      </div>

      <LogoutConfirmModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={performLogout}
      />
    </div>
  );
};
